import { useState, useEffect, useCallback } from "react";
import {
  Stack,
  Title,
  Group,
  Button,
  Table,
  TextInput,
  Modal,
  Text,
  ActionIcon,
  Badge,
  ThemeIcon,
  Loader,
  Center,
  Alert,
  Card,
  CopyButton,
  Tooltip,
  Code,
  Switch,
  Select,
} from "@mantine/core";
import {
  IconPlus,
  IconTrash,
  IconKey,
  IconAlertCircle,
  IconCopy,
  IconCheck,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { api } from "@/lib/api";
import { formatDate, formatFromNow } from "@/lib/format";

export default function ApiKeyList() {
  const [keys, setKeys] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [name, setName] = useState("");
  const [selectedProject, setSelectedProject] = useState<string>("");
  const [projects, setProjects] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);
  const [newKey, setNewKey] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [keysRes, projRes] = await Promise.all([
        api.apiKey.list(),
        api.project.select(),
      ]);
      setKeys(keysRes.data);
      setProjects(projRes.data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async () => {
    if (!name.trim() || !selectedProject) return;
    setSaving(true);
    try {
      const res = await api.apiKey.create({ name: name.trim(), project: selectedProject });
      setNewKey(res.data.key);
      setName("");
      setSelectedProject("");
      load();
    } catch (err: any) {
      notifications.show({ title: "Алдаа", message: err.message, color: "red" });
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (id: string) => {
    try {
      await api.apiKey.toggle(id);
      load();
    } catch (err: any) {
      notifications.show({ title: "Алдаа", message: err.message, color: "red" });
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await api.apiKey.remove(id);
      notifications.show({ title: "Амжилттай", message: "Устгагдлаа", color: "green" });
      load();
    } catch (err: any) {
      notifications.show({ title: "Алдаа", message: err.message, color: "red" });
    }
  };

  const baseUrl = "https://ds.goodtech.mn";

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Group gap="sm">
          <Title order={3}>API Түлхүүр</Title>
          <Badge variant="light" size="lg">{keys.length}</Badge>
        </Group>
        <Button leftSection={<IconPlus size={16} />} onClick={() => { setModalOpen(true); setNewKey(null); }} size="sm">
          Түлхүүр үүсгэх
        </Button>
      </Group>

      <Alert variant="light" color="blue" icon={<IconAlertCircle size={16} />}>
        <Stack gap="xs">
          <Text size="sm">
            API түлхүүрээр гаднаас зураг хуулж, дуудаж болно. S3-тэй адил ажиллана.
          </Text>
          <Code block>{`# Зураг хуулах
curl -X POST ${baseUrl}/s3/upload/plate \\
  -H "X-API-Key: YOUR_KEY" \\
  -F "file=@image.jpg"

# Олон зураг хуулах
curl -X POST ${baseUrl}/s3/upload/plate/batch \\
  -H "X-API-Key: YOUR_KEY" \\
  -F "files=@img1.jpg" -F "files=@img2.jpg"

# Зураг жагсаах
curl ${baseUrl}/s3/images?type=plate \\
  -H "X-API-Key: YOUR_KEY"

# Зураг дуудах
${baseUrl}/s3/{filepath}`}</Code>
        </Stack>
      </Alert>

      {loading ? (
        <Center h={200}><Loader /></Center>
      ) : keys.length === 0 ? (
        <Center h={200}>
          <Stack align="center" gap="xs">
            <ThemeIcon size={48} variant="light" color="gray" radius="xl">
              <IconKey size={24} />
            </ThemeIcon>
            <Text size="sm" c="dimmed">API түлхүүр байхгүй</Text>
          </Stack>
        </Center>
      ) : (
        <Card shadow="xs" padding={0} radius="md" withBorder>
          <Table.ScrollContainer minWidth={600}>
            <Table verticalSpacing="sm" horizontalSpacing="md" highlightOnHover>
              <Table.Thead>
                <Table.Tr style={{ backgroundColor: "var(--app-table-header)" }}>
                  <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700} w={50}>#</Table.Th>
                  <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Нэр</Table.Th>
                  <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Төсөл</Table.Th>
                  <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Түлхүүр</Table.Th>
                  <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Идэвхтэй</Table.Th>
                  <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Сүүлд ашигласан</Table.Th>
                  <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Огноо</Table.Th>
                  <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700} w={80}>Үйлдэл</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {keys.map((k, idx) => (
                  <Table.Tr key={k._id}>
                    <Table.Td c="dimmed" fz="sm">{idx + 1}</Table.Td>
                    <Table.Td><Text size="sm" fw={500}>{k.name}</Text></Table.Td>
                    <Table.Td><Badge variant="light" size="sm">{k.project?.name || "—"}</Badge></Table.Td>
                    <Table.Td>
                      <Group gap={4}>
                        <Code>{k.key.substring(0, 12)}...{k.key.slice(-4)}</Code>
                        <CopyButton value={k.key}>
                          {({ copied, copy }) => (
                            <Tooltip label={copied ? "Хуулагдлаа" : "Хуулах"}>
                              <ActionIcon size="xs" variant="subtle" color={copied ? "green" : "gray"} onClick={copy}>
                                {copied ? <IconCheck size={12} /> : <IconCopy size={12} />}
                              </ActionIcon>
                            </Tooltip>
                          )}
                        </CopyButton>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Switch
                        size="xs"
                        checked={k.isActive}
                        onChange={() => handleToggle(k._id)}
                      />
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed">
                        {k.lastUsedAt ? formatFromNow(k.lastUsedAt) : "—"}
                      </Text>
                    </Table.Td>
                    <Table.Td><Text size="xs" c="dimmed">{formatDate(k.createdAt)}</Text></Table.Td>
                    <Table.Td>
                      <ActionIcon size="sm" variant="light" radius="md" color="red" onClick={() => handleDelete(k._id)}>
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Card>
      )}

      <Modal
        opened={modalOpen}
        onClose={() => { setModalOpen(false); setNewKey(null); }}
        title={<Text fw={600}>{newKey ? "Түлхүүр үүссэн" : "API түлхүүр үүсгэх"}</Text>}
        size="md"
        centered
      >
        {newKey ? (
          <Stack>
            <Alert color="yellow" variant="light" icon={<IconAlertCircle size={16} />}>
              <Text size="sm">Энэ түлхүүрийг хадгалаарай. Дахин харуулахгүй!</Text>
            </Alert>
            <Code block>{newKey}</Code>
            <CopyButton value={newKey}>
              {({ copied, copy }) => (
                <Button
                  color={copied ? "green" : "dark"}
                  onClick={copy}
                  leftSection={copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                  size="sm"
                >
                  {copied ? "Хуулагдлаа" : "Хуулах"}
                </Button>
              )}
            </CopyButton>
          </Stack>
        ) : (
          <Stack>
            <Select
              label="Төсөл"
              placeholder="Төсөл сонгох"
              data={projects.map((p: any) => ({ value: p._id, label: p.name }))}
              value={selectedProject}
              onChange={(v) => setSelectedProject(v || "")}
              size="sm"
              required
            />
            <TextInput
              label="Нэр"
              placeholder="Жишээ: Камер 1, Mobile App"
              value={name}
              onChange={(e) => { const v = e.currentTarget.value; setName(v); }}
              size="sm"
            />
            <Button onClick={handleCreate} loading={saving} size="sm" disabled={!name.trim() || !selectedProject}>
              Үүсгэх
            </Button>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}
